import { Col, Content, FlexboxGrid, Grid, Row, Button, Text } from 'rsuite';
import PortalHeader from '../../../components/layout/PortalHeader';
import usePlaceNavigate from '../../../hooks/usePlaceNavigate';
import PersonDetails from '../../../components/form/PersonDetails';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import apiAccess from '../../../api/apiAccess';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectCurrentIDToken,
  selectCurrentUserEmail,
  selectWorkErrorMessage,
  selectWorkFailed,
  selectWorkStarted,
  selectWorkSuccess,
} from '../../../store/slices/auth/authSelectors';
import pluralize from 'pluralize';
import IconIndicator from '../../../components/user-feedback/IconIndicator';
import { selectConfigExpressRegistrationTypeVerbiage } from '../../../store/slices/config/configSelectors';
import { updateAccountDetails } from '../../../store/slices/auth/authThunks';
import Notice from '../../../components/user-feedback/Notice';
import {
  companyNameValidation,
  phoneRequiredValidation,
  requiredValidation,
} from '../../../common/validation';
import { isNullUndefinedOrEmptyObject } from '../../../common/utils';
import { ConfirmationModal } from './ConfirmationModal';

const UpdateAccount = () => {
  const placeNavigate = usePlaceNavigate();
  const form = useRef();
  const dispatch = useDispatch();

  const [formError, setFormError] = useState({});
  const [formValues, setFormValues] = useState({});
  const [addressComponents, setAddressComponents] = useState({});
  const [currentUserDetails, setCurrentUserDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [errorMessageVisible, setErrorMessageVisible] = useState(false);

  const idToken = useSelector(selectCurrentIDToken);
  const email = useSelector(selectCurrentUserEmail);
  const permitType = useSelector(selectConfigExpressRegistrationTypeVerbiage);

  const updatingDetails = useSelector(selectWorkStarted);
  const updatingDetailsSuccess = useSelector(selectWorkSuccess);
  const updatingDetailsFail = useSelector(selectWorkFailed);
  const updatingDetailsErrorMessage = useSelector(selectWorkErrorMessage);

  const regVerbiage = useSelector(selectConfigExpressRegistrationTypeVerbiage);

  useEffect(() => {
    const getAccountDetails = async () => {
      if (idToken && email) {
        setLoadingDetails(true);
        const userDetails = await apiAccess.getUserDetailsAuthTokenAsync(
          email,
          idToken
        );
        setCurrentUserDetails(userDetails);
      }
    };
    getAccountDetails();
  }, [idToken, email]);

  // set loadingDetails to false here, otherwise there is an issue with it being updated prior to userDetails update being finished
  useEffect(() => {
    if (loadingDetails && !isNullUndefinedOrEmptyObject(currentUserDetails)) {
      setLoadingDetails(false);
    }
  }, [currentUserDetails, loadingDetails]);

  const formModel = useMemo(() => {
    return {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      companyName: '',
      mailingAddress: '',
    };
  }, []);

  useEffect(() => {
    const addressComponents =
      currentUserDetails?.misc?.mailingAddressComponents ?? {};
    setAddressComponents(addressComponents);
  }, [currentUserDetails]);

  useEffect(() => {
    if (!isNullUndefinedOrEmptyObject(currentUserDetails)) {
      setFormValues({
        email: currentUserDetails?.email ?? '',
        firstName: currentUserDetails?.firstName ?? '',
        lastName: currentUserDetails?.lastName ?? '',
        phone: currentUserDetails?.phone ?? '',
        companyName: currentUserDetails?.companyName ?? '',
        mailingAddress: currentUserDetails?.mailingAddress ?? '',
      });
    }
  }, [currentUserDetails]);

  // Validation
  const validationModel = {
    // don't need validation for disabled inputs
    phone: phoneRequiredValidation,
    companyName: companyNameValidation,
    mailingAddress: requiredValidation,
  };

  const onUnrestrictedAddressSelected = ({ components }) => {
    setAddressComponents(components);
  };

  const validateForm = () => {
    return form.current.check();
  };

  const openConfirmationModal = () => {
    const isValid = validateForm();

    if (isValid) {
      setConfirmationModalOpen(true);
    }
  };

  const updatePersonalDetails = async () => {
    setSuccessMessageVisible(false);
    setErrorMessageVisible(false);

    const isValid = validateForm();

    if (isValid) {
      const updatedData = {
        ...formValues,
        mailingAddressComponents: addressComponents,
      };
      dispatch(updateAccountDetails(currentUserDetails?.userId, updatedData));
    }
  };

  useEffect(() => {
    setConfirmationModalOpen(false);
    if (updatingDetailsSuccess) {
      setSuccessMessageVisible(true);
      const timer = setTimeout(() => {
        setSuccessMessageVisible(false);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    } else {
      setSuccessMessageVisible(false);
    }
  }, [updatingDetailsSuccess]);

  useEffect(() => {
    setConfirmationModalOpen(false);
    setErrorMessageVisible(!!updatingDetailsFail);
  }, [updatingDetailsFail]);

  return (
    <>
      <FlexboxGrid justify="space-between">
        <FlexboxGrid.Item colspan={24}>
          <PortalHeader title={`${regVerbiage} Registration Application`} />
        </FlexboxGrid.Item>
        <FlexboxGrid.Item
          colspan={24}
          className="sm-page-padding"
        >
          <Content>
            <Grid className="mt-24">
              <Row className="mb-24">
                <Col xs={24}>
                  <FlexboxGrid>
                    <FlexboxGrid.Item style={{ paddingRight: '20px' }}>
                      <Button
                        appearance="subtle"
                        className="btn-link-underline-hover"
                        onClick={() => {
                          placeNavigate(-1);
                        }}
                      >
                        &lt; Back
                      </Button>
                    </FlexboxGrid.Item>
                  </FlexboxGrid>
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <FlexboxGrid>
                    <FlexboxGrid.Item>
                      <Text className="header-text-h3 mb-24">
                        Update Details
                      </Text>
                    </FlexboxGrid.Item>
                  </FlexboxGrid>
                </Col>
              </Row>
              {loadingDetails && <IconIndicator />}

              {!loadingDetails ? (
                <>
                  <Row className="content-section">
                    <Col xs={24}>
                      <div role="grid">
                        <Row className="mb-24">
                          <Col xs={24}>
                            <Text>
                              Please update your details below. Note that you
                              cannot change some details.
                            </Text>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={24}>
                            <PersonDetails
                              formModel={formModel}
                              ref={form}
                              setFormError={setFormError}
                              setFormValues={setFormValues}
                              formValues={formValues}
                              formError={formError}
                              validationModel={validationModel}
                              emailDisabled
                              firstNameDisabled
                              lastNameDisabled
                              onUnrestrictedAddressLookupChange={
                                onUnrestrictedAddressSelected
                              }
                            />
                          </Col>
                        </Row>

                        {updatingDetails && (
                          <Row>
                            <Col xs={24}>
                              <IconIndicator />
                            </Col>
                          </Row>
                        )}

                        {successMessageVisible && !updatingDetails && (
                          <Row className="mb-12">
                            <Col xs={24}>
                              <Notice
                                type="success"
                                message="Your details have been updated successfully."
                              />
                            </Col>
                          </Row>
                        )}

                        {errorMessageVisible && !updatingDetails && (
                          <Row className="mb-12">
                            <Col xs={24}>
                              <Notice message={updatingDetailsErrorMessage} />
                            </Col>
                          </Row>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col
                      xs={24}
                      className="mt-16"
                    >
                      <Button
                        className="btn-primary sm-100-percent"
                        data-cy="update-details-form-button"
                        onClick={() => {
                          openConfirmationModal();
                        }}
                      >
                        Update Details
                      </Button>
                    </Col>
                  </Row>
                </>
              ) : null}
            </Grid>
          </Content>
        </FlexboxGrid.Item>
      </FlexboxGrid>
      <ConfirmationModal
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        permitType={pluralize(permitType).toLocaleLowerCase()}
        loading={updatingDetails}
        onAccept={updatePersonalDetails}
      />
    </>
  );
};

export default UpdateAccount;
